import { rgbToHex } from './colorUtils';

const MAX_SIZE = 96;
const SAMPLE_STEP = 4;

const normalizeBucket = value => Math.floor(value / 16) * 16;

const isNearGray = (r, g, b) => {
  const delta = Math.max(r, g, b) - Math.min(r, g, b);
  return delta < 12;
};

const shouldIgnorePixel = (r, g, b, a) => {
  if (a < 180) return true;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (min > 242) return true;
  if (max < 16) return true;

  return false;
};

const loadImage = (logoUrl, timeoutMs = 8000) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    let done = false;
    image.crossOrigin = 'anonymous';

    const finish = handler => event => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      image.onabort = null;
      handler(event);
    };

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      image.onload = null;
      image.onerror = null;
      image.onabort = null;
      reject(new Error(`Image load timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    image.onload = finish(() => resolve(image));
    image.onerror = finish(() => reject(new Error('Image load failed')));
    image.onabort = finish(() => reject(new Error('Image load aborted')));
    image.src = logoUrl;
  });

export const extractPaletteFromLogo = async logoUrl => {
  if (!logoUrl) return [];

  const image = await loadImage(logoUrl);
  const scale = Math.min(1, MAX_SIZE / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
  const width = Math.max(1, Math.round((image.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || 1) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [];

  ctx.drawImage(image, 0, 0, width, height);

  let data;
  try {
    ({ data } = ctx.getImageData(0, 0, width, height));
  } catch (_error) {
    // Canvas foi "tainted" por origem cruzada sem CORS.
    throw new Error('Logo host without CORS permission for pixel extraction');
  }
  const buckets = new Map();

  for (let i = 0; i < data.length; i += 4 * SAMPLE_STEP) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (shouldIgnorePixel(r, g, b, a)) continue;

    const rb = normalizeBucket(r);
    const gb = normalizeBucket(g);
    const bb = normalizeBucket(b);

    // Mantem alguns cinzas apenas quando muito escuros para contraste; evita fundo lavado.
    if (isNearGray(rb, gb, bb) && rb > 170) continue;

    const key = `${rb}-${gb}-${bb}`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }

  const sorted = Array.from(buckets.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const total = sorted.reduce((sum, [, weight]) => sum + weight, 0) || 1;

  return sorted.map(([key, weight]) => {
    const [r, g, b] = key.split('-').map(Number);
    return {
      hex: rgbToHex({ r, g, b }),
      weight: weight / total
    };
  });
};

export default extractPaletteFromLogo;
